import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Avatar } from './Avatar'

interface LayoutProps {
  children: React.ReactNode
}

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Verify Claims' },
  { to: '/profile', label: 'Profile' },
  { to: '/community', label: 'Discerning Minds' },
] as const

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [showMenu, setShowMenu] = useState(false)

  const handleLogout = async () => {
    try {
      await logout()
    } catch {
      // Error handled by auth hook
    }
  }

  // Get user level based on verification count (matching mobile app)
  const getUserLevel = (verifications: number) => {
    if (verifications >= 500) return { name: 'Wisdom Curator' }
    if (verifications >= 201) return { name: 'Discerning Mind' }
    if (verifications >= 51) return { name: 'Critical Thinker' }
    return { name: 'Curious Observer' }
  }

  const userLevel = getUserLevel(user?.monthlyChecksUsed || user?.trialChecksUsed || 0)

  const showPremiumBadge = (to: string) =>
    to === '/community' &&
    (user?.subscriptionTier === 'free' || user?.subscriptionTier === 'basic' || !user?.subscriptionTier)

  return (
    <div className="bg-paper-0 min-h-screen">
      {/* Fixed header */}
      <div className="fixed top-0 left-0 right-0 z-[100] h-16 md:h-20 px-4 md:px-8 flex items-center justify-center bg-brand-950 border-b border-white/10">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="absolute left-4 md:left-8 w-11 h-11 flex items-center justify-center rounded-md text-white text-xl border border-white/15 bg-white/5 hover:bg-white/10 transition-colors duration-150"
          aria-label="Open menu"
        >
          ≡
        </button>

        <Link to="/dashboard" className="flex items-center justify-center">
          <img
            src="/assets/logo-white.png"
            alt="Treu"
            className="h-6 md:h-7 w-auto"
          />
        </Link>
      </div>

      {/* Slide-out menu */}
      {showMenu && (
        <div className="fixed inset-0 z-[1000] flex">
          <div className="w-[80%] max-w-[320px] h-full bg-white shadow-overlay flex flex-col">
            {/* Menu header with user profile */}
            <div className="bg-brand-950 pt-14 pb-5 px-5">
              <div className="flex items-center">
                <div className="mr-3">
                  <Avatar size="large" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-base font-semibold mb-1 truncate">
                    {user?.name || user?.email?.split('@')[0] || 'User'}
                  </div>
                  <div className="text-white/70 text-xs tracking-wide">
                    {userLevel.name}
                  </div>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="pt-5">
              {NAV_ITEMS.map(({ to, label }) => {
                const active = location.pathname === to
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setShowMenu(false)}
                    className={`flex items-center min-h-[44px] px-5 py-4 no-underline transition-colors duration-150 ${
                      active
                        ? 'text-brand-950 bg-brand-50 border-l-4 border-brand-950 font-semibold'
                        : 'text-ink-700 hover:bg-paper-50'
                    }`}
                  >
                    <span className="text-base flex-1">{label}</span>
                    {showPremiumBadge(to) && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium text-brand-950 bg-brand-100">
                        Premium
                      </span>
                    )}
                  </Link>
                )
              })}

              <div className="h-px bg-paper-200 my-2 mx-5" />

              <button
                onClick={handleLogout}
                className="flex items-center w-full min-h-[44px] px-5 py-4 text-base text-ink-700 hover:bg-paper-50 transition-colors duration-150"
              >
                <span>Sign Out</span>
              </button>
            </div>
          </div>
          <div
            onClick={() => setShowMenu(false)}
            className="flex-1 bg-black/40"
          />
        </div>
      )}

      {/* Content area */}
      <main className="pt-16 md:pt-[84px]">
        {children}
      </main>
    </div>
  )
}

export default Layout
